import { useCallback, useMemo } from 'react'
import { useParams } from '@fs/zion-router'
import { type Contributions, type Contribution } from '@fs/zion-tree-contributions'
import { type RelationshipType, type PID } from '@fs/zion-tree-types'
import { type Persons } from '@fs/zion-tree-contributions/dist/es/types/persons'
import { type SWRInfiniteKeyLoader } from 'swr/infinite'
import { useTranslation } from 'react-i18next'
import {
  FILTER_GROUPS,
  findFilterInFilterGroups,
  isEventOpsFilterChoice,
  isConclusionFilterChoice,
  isEntityRefFilterChoice,
} from './change-log-service-ts'
import { useSwrInfiniteRequest } from '../../data-service/useSwrRequest'
import { type FilterChoice, type FilterChoiceType } from '../types'

type UseContributionsProps = {
  pageSize?: number
}

type FetchNextPage = (nextPageToken: string) => void

type UseContributionsReturnType = {
  contributions: Contribution[]
  persons: Contributions['persons']
  nextPageToken: Contributions['nextPageToken']
  refetchContributions: ReturnType<typeof useSwrInfiniteRequest<Contributions>>['mutate']
  fetchNextPage: FetchNextPage
  errorMessage: string | undefined
  filterType: FilterChoiceType
  pid: PID
  relationshipId?: PID
  relationshipType?: RelationshipType
  initialLoading: boolean
  isValidatingNextPage: boolean
}

const CURSOR_PARAM = 'from'
const NEXT_CURSOR_TOKEN_NAME = 'nextPageToken' // This should match the server's response field for the next cursor token

export function useContributions({ pageSize }: UseContributionsProps): UseContributionsReturnType {
  const [t] = useTranslation()
  const { filterType, pid, relationshipType, relationshipId } = useParams() as {
    filterType?: FilterChoiceType
    pid: PID
    relationshipType?: RelationshipType
    relationshipId?: PID
  }

  const normalizedFilterType = filterType?.toUpperCase().replace(/-/g, '_') as FilterChoiceType | undefined
  const selectedFilter: FilterChoice = findFilterInFilterGroups(normalizedFilterType) ?? FILTER_GROUPS.ALL[0]

  const baseUrl = relationshipId
    ? `/service/tree/tree-data/contributions/${relationshipType}/${relationshipId}`
    : `/service/tree/tree-data/contributions/person/${pid}`

  const params = useMemo(() => {
    const searchParams = new URLSearchParams({ useHistoryPersons: 'true' })

    if (pageSize) {
      searchParams.append('pageSize', pageSize.toString())
    }

    if (selectedFilter && !relationshipId) {
      if (isEventOpsFilterChoice(selectedFilter)) {
        selectedFilter.eventOps.forEach((eventOp) => {
          searchParams.append('eventOp', eventOp)
        })
      } else if (isConclusionFilterChoice(selectedFilter)) {
        if (selectedFilter.conclusionType === 'FACT') {
          searchParams.append('factType', selectedFilter.constantCaseFactType)
        } else {
          searchParams.append('conclusionType', selectedFilter.conclusionType)
        }
      } else if (isEntityRefFilterChoice(selectedFilter)) {
        searchParams.append('entityRefType', selectedFilter.entityRefType)
      }
    }

    return searchParams
  }, [pageSize, selectedFilter, relationshipId])

  const getKey: SWRInfiniteKeyLoader<Contributions> = useCallback(
    (pageIndex, previousPageData) => {
      if (pageIndex === 0 || !previousPageData) {
        const allParamsToAdd = params?.size ? `?${params.toString()}` : ''
        return `${baseUrl}${allParamsToAdd}`
      }
      const paramsToAdd = params?.size ? `&${params.toString()}` : ''
      const urlWithCursor = `${baseUrl}?${CURSOR_PARAM}=${previousPageData[NEXT_CURSOR_TOKEN_NAME]}${paramsToAdd}`
      return urlWithCursor
    },
    [baseUrl, params]
  )

  const { data, isLoading, setSize, error, isValidating, size, mutate } = useSwrInfiniteRequest<Contributions>(getKey)

  const fetchNextPage = useCallback(() => {
    setSize((prevSize) => prevSize + 1)
  }, [setSize])

  const contributions = useMemo(
    () =>
      data && Array.isArray(data) ? (data?.flatMap((contributionsItem) => contributionsItem.contributions) ?? []) : [],
    [data]
  )

  const persons = useMemo(
    () =>
      data && Array.isArray(data)
        ? (data?.reduce<Persons>((accum, contributionsItem) => ({ ...accum, ...contributionsItem.persons }), {}) ?? {})
        : {},
    [data]
  )

  return {
    contributions,
    persons,
    nextPageToken: data && Array.isArray(data) ? data?.at(-1)?.nextPageToken : undefined,
    errorMessage: error ? t('change-log.unable-to-load-list.error') : undefined,
    filterType: selectedFilter.value,
    pid,
    initialLoading: isLoading,
    isValidatingNextPage: isValidating && size > 1,
    relationshipId,
    relationshipType,
    refetchContributions: mutate,
    fetchNextPage,
  }
}

export const emptyUseContributionsReturn = {
  contributions: [],
  persons: {},
  nextPageToken: undefined,
  refetchContributions: () => Promise.resolve([]),
  errorMessage: undefined,
  filterType: 'ALL',
  relationshipId: undefined,
  relationshipType: undefined,
  fetchNextPage: () => undefined,
  initialLoading: true,
  isValidatingNextPage: false,
} satisfies Omit<ReturnType<typeof useContributions>, 'pid'>
